import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiClient } from "@/App";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Mail,
  Phone,
  MapPin,
  Building2,
  Eye,
  Pencil,
  Trash2,
  X,
  Cake,
  UserPlus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

export default function Clients() {
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showNewModal, setShowNewModal] = useState(location.state?.openNew || false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    project_name: "",
    project_type: "architecture",
    site_location: "",
    plot_area: "",
    estimated_budget: "",
    notes: "",
    birthday: null,
    additional_emails: []
  });
  
  // Additional email form
  const [newAdditionalEmail, setNewAdditionalEmail] = useState({ name: "", email: "", role: "" });

  useEffect(() => {
    fetchClients();
  }, [statusFilter, typeFilter]);

  const fetchClients = async () => {
    try {
      let url = "/clients";
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (typeFilter !== "all") params.append("project_type", typeFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await apiClient.get(url);
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const data = {
        ...formData,
        estimated_budget: formData.estimated_budget ? parseFloat(formData.estimated_budget) : null,
        birthday: formData.birthday ? format(formData.birthday, "yyyy-MM-dd") : null
      };
      
      await apiClient.post("/clients", data);
      toast.success("Client enquiry added successfully!");
      setShowNewModal(false);
      resetForm();
      fetchClients();
    } catch (error) {
      // Handle different error formats
      const errorData = error.response?.data;
      let errorMessage = "Failed to add client";
      
      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData?.detail) {
        if (typeof errorData.detail === "string") {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors
          errorMessage = errorData.detail.map(err => err.msg || err.message).join(", ");
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const addAdditionalEmail = () => {
    if (!newAdditionalEmail.email) {
      toast.error("Please enter an email address");
      return;
    }
    setFormData({
      ...formData,
      additional_emails: [...formData.additional_emails, { ...newAdditionalEmail }]
    });
    setNewAdditionalEmail({ name: "", email: "", role: "" });
  };

  const removeAdditionalEmail = (index) => {
    setFormData({
      ...formData,
      additional_emails: formData.additional_emails.filter((_, i) => i !== index)
    });
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    
    try {
      await apiClient.delete(`/clients/${clientId}`);
      toast.success("Client deleted successfully");
      fetchClients();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      project_name: "",
      project_type: "architecture",
      site_location: "",
      plot_area: "",
      estimated_budget: "",
      notes: "",
      birthday: null,
      additional_emails: []
    });
    setNewAdditionalEmail({ name: "", email: "", role: "" });
  };

  const filteredClients = clients.filter(client => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.project_name?.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status) => {
    const styles = {
      enquiry: "status-enquiry",
      proposal_sent: "status-pending",
      accepted: "status-active",
      active: "status-active",
      completed: "status-completed"
    };
    return `badge ${styles[status] || "bg-gray-700 text-gray-300"}`;
  };

  const getTypeBadge = (type) => {
    return `badge type-${type}`;
  };

  return (
    <div className="page-content" data-testid="clients-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">Manage your client enquiries and projects</p>
        </div>
        <Button 
          onClick={() => setShowNewModal(true)}
          className="bg-[#FF4500] hover:bg-[#E65100] btn-glow"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Enquiry
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#171717] border-[#262626]"
            data-testid="search-input"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] bg-[#171717] border-[#262626]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#262626]">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="enquiry">Enquiry</SelectItem>
            <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px] bg-[#171717] border-[#262626]" data-testid="type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#262626]">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="architecture">Architecture</SelectItem>
            <SelectItem value="interior">Interior</SelectItem>
            <SelectItem value="turnkey">Turnkey</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Client List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-48 bg-[#1A1A1A] rounded animate-pulse"></div>
          ))}
        </div>
      ) : filteredClients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
          {filteredClients.map((client) => (
            <div
              key={client.client_id}
              className="bg-[#1A1A1A] border border-[#262626] rounded p-5 card-hover relative group"
              data-testid={`client-card-${client.client_id}`}
            >
              {/* Actions Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#262626] opacity-0 group-hover:opacity-100 transition-opacity">
                    <MoreVertical className="w-4 h-4 text-gray-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#1A1A1A] border-[#262626]">
                  <DropdownMenuItem asChild>
                    <Link to={`/clients/${client.client_id}`} className="flex items-center gap-2 cursor-pointer">
                      <Eye className="w-4 h-4" /> View Details
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleDelete(client.client_id)}
                    className="text-red-400 focus:text-red-400 cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Client Info */}
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FF4500] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold">{client.name?.charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/clients/${client.client_id}`}>
                    <h3 className="font-semibold text-white truncate hover:text-[#FF4500] transition-colors">
                      {client.name}
                    </h3>
                  </Link>
                  <p className="text-sm text-gray-500 truncate">{client.project_name}</p>
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className={getStatusBadge(client.status)}>{client.status?.replace("_", " ")}</span>
                <span className={getTypeBadge(client.project_type)}>{client.project_type}</span>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-400">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{client.email}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Phone className="w-4 h-4" />
                  <span>{client.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <MapPin className="w-4 h-4" />
                  <span className="truncate">{client.site_location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state" data-testid="empty-state">
          <div className="empty-state-icon">
            <Users />
          </div>
          <h3 className="empty-state-title">No clients found</h3>
          <p className="empty-state-description">
            {searchQuery || statusFilter !== "all" || typeFilter !== "all"
              ? "Try adjusting your filters"
              : "Add your first client enquiry to get started"}
          </p>
          {!searchQuery && statusFilter === "all" && typeFilter === "all" && (
            <Button onClick={() => setShowNewModal(true)} className="bg-[#FF4500] hover:bg-[#E65100]">
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          )}
        </div>
      )}

      {/* New Client Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-2xl max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">New Client Enquiry</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Add a new client enquiry to the system
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Client Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Client Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Client name"
                    className="bg-[#171717] border-[#262626]"
                    required
                    data-testid="client-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Primary Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="client@email.com"
                    className="bg-[#171717] border-[#262626]"
                    required
                    data-testid="client-email-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Phone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 XXXXX XXXXX"
                    className="bg-[#171717] border-[#262626]"
                    required
                    data-testid="client-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 flex items-center gap-1">
                    <Cake className="w-3 h-3" /> Birthday
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal bg-[#171717] border-[#262626] text-white"
                        data-testid="client-birthday-input"
                      >
                        {formData.birthday ? format(formData.birthday, "MMM d, yyyy") : "Select birthday"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border-[#262626]">
                      <Calendar
                        mode="single"
                        selected={formData.birthday}
                        onSelect={(date) => setFormData({...formData, birthday: date})}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-400">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Client address"
                    className="bg-[#171717] border-[#262626]"
                  />
                </div>
              </div>
            </div>
            
            {/* Additional Emails */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <UserPlus className="w-4 h-4" /> Additional Contacts
              </h4>
              <div className="bg-[#111111] rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    value={newAdditionalEmail.name}
                    onChange={(e) => setNewAdditionalEmail({...newAdditionalEmail, name: e.target.value})}
                    placeholder="Contact name"
                    className="bg-[#171717] border-[#262626]"
                  />
                  <Input
                    type="email"
                    value={newAdditionalEmail.email}
                    onChange={(e) => setNewAdditionalEmail({...newAdditionalEmail, email: e.target.value})}
                    placeholder="Email address"
                    className="bg-[#171717] border-[#262626]"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={newAdditionalEmail.role}
                      onChange={(e) => setNewAdditionalEmail({...newAdditionalEmail, role: e.target.value})}
                      placeholder="Role (e.g., Spouse)"
                      className="bg-[#171717] border-[#262626] flex-1"
                    />
                    <Button 
                      type="button" 
                      onClick={addAdditionalEmail}
                      variant="outline"
                      size="icon"
                      className="border-[#262626] hover:bg-[#262626]"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {formData.additional_emails.length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-[#262626]">
                    {formData.additional_emails.map((email, i) => (
                      <div key={i} className="flex items-center justify-between bg-[#1A1A1A] p-2 rounded">
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="text-white text-sm">{email.name || "No name"} - {email.email}</p>
                            {email.role && <p className="text-gray-500 text-xs">{email.role}</p>}
                          </div>
                        </div>
                        <button 
                          type="button"
                          onClick={() => removeAdditionalEmail(i)}
                          className="p-1 hover:bg-[#262626] rounded"
                        >
                          <X className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">Add family members or site engineers who should also receive updates</p>
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Project Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">Project Name *</Label>
                  <Input
                    value={formData.project_name}
                    onChange={(e) => setFormData({...formData, project_name: e.target.value})}
                    placeholder="e.g., Residence, Office"
                    className="bg-[#171717] border-[#262626]"
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Project Type *</Label>
                  <Select 
                    value={formData.project_type} 
                    onValueChange={(value) => setFormData({...formData, project_type: value})}
                  >
                    <SelectTrigger className="bg-[#171717] border-[#262626]" data-testid="project-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                      <SelectItem value="architecture">Architecture</SelectItem>
                      <SelectItem value="interior">Interior Only</SelectItem>
                      <SelectItem value="turnkey">Turnkey Project</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-400">Site Location *</Label>
                  <Input
                    value={formData.site_location}
                    onChange={(e) => setFormData({...formData, site_location: e.target.value})}
                    placeholder="City, State"
                    className="bg-[#171717] border-[#262626]"
                    required
                    data-testid="site-location-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Plot Area</Label>
                  <Input
                    value={formData.plot_area}
                    onChange={(e) => setFormData({...formData, plot_area: e.target.value})}
                    placeholder="e.g., 4000 sqft"
                    className="bg-[#171717] border-[#262626]"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-400">Estimated Budget (₹)</Label>
                  <Input
                    type="number"
                    value={formData.estimated_budget}
                    onChange={(e) => setFormData({...formData, estimated_budget: e.target.value})}
                    placeholder="Enter amount"
                    className="bg-[#171717] border-[#262626]"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-gray-400">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes about the client or project..."
                className="bg-[#171717] border-[#262626] min-h-[100px]"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewModal(false)}
                className="border-[#262626] hover:bg-[#262626]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#FF4500] hover:bg-[#E65100]"
                data-testid="submit-client-btn"
              >
                {submitting ? "Adding..." : "Add Client"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
